import React, { useCallback, useImperativeHandle, forwardRef, useMemo, useState } from 'react';
import type { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Link,
    Typography,
    Badge,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useDataProvider, useNotify } from 'react-admin';
import AgGrid from '../../../../components/ag-grid/ag-grid';
import { AISingleEventAnalysis, EventItem, RawEventData } from '../../models/event';
import { PrepareData } from './SingleEventAnalysisTab';
import { MarketVelocityFeatures, PrepareDataResponse } from './utils/SingleEventAnalysisTab.types';
import '../../css/style.css';
/**
 * Extended DataProvider interface with custom postData method
 */
interface ExtendedDataProvider {
    postData: <T = unknown>(resource: string, action: string, data: unknown, method?: string) => Promise<{ data: T }>;
    currentUser: () => Promise<any>;
    fetchData: <T = unknown>(resource: string, query: string) => Promise<{ data: T }>;
}

/**
 * Notification function type
 */
type NotifyFn = (message: string, options?: { type?: 'success' | 'info' | 'warning' | 'error' }) => void;

/**
 * Score data structure
 */
interface Scores {
    overallScore: number;
    totalPositiveScore: number;
    totalRiskScore: number;
    totalDefaultScore: number;
}

/**
 * Recommendation structure
 */
interface Recommendation {
    decision: 'Strong Buy' | 'Buy' | 'Hold' | 'Ignore';
    confidenceLevel: string;
}

export interface RecommendationCount {
  name: string;
  count: number;
}

/**
 * Bulk analysis result item
 */
export interface BulkAnalysisResult {
    analysisTimestamp?: string;
    eventId: bigint;
    siteId: string;
    url: string;
    eventName: string;
    eventDate: string;
    factors?: {
        positiveFactors?: string[];
        riskFactors?: string[];
    };
    recommendation: Recommendation;
    venue: string;
    city?: string;
    state?: string;
    country?: string;
    scores: Scores;
    velocitySnapshot?: MarketVelocityFeatures;
}

/**
 * Component props for BulkAnalysisTab
 */
interface BulkAnalysisTabProps {
    getRecommendationIcon: (level: string) => React.ReactNode;
    getScoreColor: (score: number) => 'success' | 'warning' | 'error' | 'default' | 'primary' | 'secondary' | 'info';
    bulkAnalysis: BulkAnalysisResult[];
    selectedEvents: EventItem[];
    isLoading: boolean;
    recommendationCount: RecommendationCount[];
    setRecommendationCount: React.Dispatch<React.SetStateAction<RecommendationCount[]>>;
    setActiveTab: (tab: number) => void;
    setSelectedEvent: (event: EventItem | null) => void;
    setAnalysisBulkTarget: (event: EventItem | null) => void;
}

/**
 * Ref handle methods exposed to parent
 */
export interface BulkAnalysisTabHandle {
    runBulkAnalysis: (events: RawEventData[]) => Promise<BulkAnalysisResult[]>;
}

/**
 * BulkAnalysisTab Component
 * Manages bulk event analysis functionality as a dedicated component.
 * Handles API interactions and UI rendering for bulk analysis results.
 * The recommendationCount state is managed by the parent component for shared state access.
 *
 * @param props - Component props
 * @param ref - React ref for exposing imperative methods
 * @returns Bulk analysis tab component
 */
const BulkAnalysisTab = forwardRef<BulkAnalysisTabHandle, BulkAnalysisTabProps>((props, ref) => {
    const {
        getRecommendationIcon,
        getScoreColor,
        bulkAnalysis,
        selectedEvents,
        isLoading,
        recommendationCount,
        setRecommendationCount,
        setActiveTab,
        setAnalysisBulkTarget,
        setSelectedEvent } = props;

    const REAL_TICKET_API_URL = process.env.REAL_TICKET_API_URL || 'https://localhost:5002';
    const notify = useNotify() as NotifyFn;
    const dataProvider = useDataProvider() as unknown as ExtendedDataProvider;

    // State for recommendation filter - tracks which recommendation types are selected
    const [recommendationFilter, setRecommendationFilter] = useState<string[]>([]);

    /**
     * Configuration for recommendation filter checkboxes with color coding.
     */
    const recommendationFilterData = useMemo(() => [
        { name: 'Strong Buy', label: 'Strong Buy', color: 'success' as const },
        { name: 'Buy', label: 'Buy', color: 'success' as const },
        { name: 'Hold', label: 'Hold', color: 'warning' as const },
        { name: 'Ignore', label: 'Ignore', color: 'error' as const }
    ], []);

    /**
     * Handles instant filter changes when checkboxes are toggled.
     * Updates the filter state immediately without requiring a search button.
     * @param recommendationName - Name of the recommendation type being toggled
     * @param checked - Whether the checkbox is checked or unchecked
     */
    const handleCheckboxChange = useCallback((recommendationName: string, checked: boolean) => {
        setRecommendationFilter(prev => {
            if (checked) {
                // Add the recommendation to the filter if checked
                return [...prev, recommendationName];
            } else {
                // Remove the recommendation from the filter if unchecked
                return prev.filter(name => name !== recommendationName);
            }
        });
    }, []);

    /**
     * Handles click on View button to navigate to Single Analysis tab
     * @param eventId - The event ID to view
     */
    const handleViewClick = useCallback(async (siteId: string) => {
        // Find the matching event in selectedEvents by eventId
        const matchingEvent = selectedEvents.find(ev => ev.siteId === siteId);
        if (matchingEvent) {
            setSelectedEvent(matchingEvent);
            setAnalysisBulkTarget(matchingEvent); // Set the selected event for Single Analysis tab to display analysis result immediately
            setActiveTab(1); // Switch to Single Analysis tab
            // Fetch latest analysis data and prepare data for the selected event to get seat analysis info if needed
            const latestAnalysisResponse = await dataProvider.fetchData<AISingleEventAnalysis>('HistoryAnalysis', `GetSingleEventAnalysis?siteId=${matchingEvent.siteId}`);
            const prepareResp = await dataProvider.postData<PrepareDataResponse>('RTP_Event', 'PrepareScoreEvent', matchingEvent);
            if (latestAnalysisResponse.data.id && prepareResp.data.eventData) {
                setAnalysisBulkTarget({ ...matchingEvent, analysisId: latestAnalysisResponse.data.id, prepareData: { ...prepareResp.data.eventData, haveSeatAnalysis: latestAnalysisResponse.data.haveSeatAnalysis } });
            }
            
        }
    }, [selectedEvents, setSelectedEvent, setActiveTab, setAnalysisBulkTarget]);

    /**
     * Filters bulk analysis results based on selected recommendations
     * @returns Filtered bulk analysis results
     */
    const filteredBulkAnalysis = useMemo(() => {
        if (recommendationFilter.length === 0) {
            return bulkAnalysis;
        }
        return bulkAnalysis.filter(result =>
            recommendationFilter.includes(result.recommendation.decision)
        );
    }, [bulkAnalysis, recommendationFilter]);

    /**
     * Default column definition for AgGrid with common settings
     */
    const defaultColDef = useMemo<ColDef>(() => {
        return {
            sortable: true,
            resizable: true,
            flex: 1,
            minWidth: 50,
            filter: false,
            suppressMovable: true,
            singleClickEdit: true,
        };
    }, []);

    /**
     * Renders event name and ID in the Event column with clickable link
     */
    const renderEventCell = (params: ICellRendererParams<BulkAnalysisResult>): React.ReactNode => {
        return (
            <Box>
                <Link
                    href={params.data?.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    underline='hover'
                    style={{
                        textDecoration: 'underline',
                        color: '#58b0f7',
                        fontWeight: '400'
                    }}
                >
                    {params.data?.eventName}
                </Link>
                <br />
                <span style={{ color: 'gray' }}>SiteID: {params.data?.siteId}</span>
            </Box>
        );
    };

    /**
     * Renders date and time in the Date column following SearchEventTable format
     */
    const renderDateCell = (params: ICellRendererParams<BulkAnalysisResult>): React.ReactNode => {
        return (
            <>
                {dayjs(params.data?.eventDate).format('YYYY-MM-DD')}{' | '}{dayjs(params.data?.eventDate).format('hh:mm A')}
                <br />
                <span style={{ color: 'gray' }}>{`(${dayjs(params.data?.eventDate).diff(dayjs(), 'day')} days remain)`}</span>
            </>
        );
    };

    /**
     * Renders venue name on the first line and city, state on the second line in the Venue column.
     * Gracefully handles missing city or state information by only displaying available data.
     * City and state are retrieved from the BulkAnalysisResult data enriched during analysis.
     */
    const renderVenueCell = (params: ICellRendererParams<BulkAnalysisResult>): React.ReactNode => {
        // Get city and state from the bulk analysis result data (enriched during analysis)
        const city = params.data?.city;
        const state = params.data?.state;
        const country = params.data?.country;

        // Build location string if city or state is available
        const locationParts: string[] = [];
        if (city) locationParts.push(city);
        if (state) locationParts.push(state);
        if (country) locationParts.push(country);
        const location = locationParts.join(', ');
        
        return (
            <span style={{ whiteSpace: 'normal' }}>
                {params.data?.venue}
                {location && (
                    <>
                        <br />
                        {location}
                    </>
                )}
            </span>
        );
    };

    /**
     * Renders overall score as a color-coded chip with View button
     */
    const renderScoreChip = (params: ICellRendererParams<BulkAnalysisResult>): React.ReactNode => {
        if (!params.data?.scores) return null;
        
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip
                    label={params.data.scores.overallScore.toFixed(1)}
                    color={getScoreColor(params.data.scores.overallScore)}
                    size="small"
                />
                <Tooltip title="View in Single Analysis">
                    <IconButton
                        size="small"
                        onClick={() => params.data && handleViewClick(params.data.siteId)}
                        sx={{ padding: '2px' }}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    };

    /**
     * Renders recommendation with icon and text
     */
    const renderRecommendationCell = (params: ICellRendererParams<BulkAnalysisResult>): React.ReactNode => {
        if (!params.data?.recommendation) return null;
        
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 1 }}>
                {getRecommendationIcon(params.data.recommendation.decision)}
                <Chip
                    label={params.data.recommendation.decision}
                    color={getScoreColor(params.data.scores?.overallScore || 0)}
                />
            </Box>
        );
    };

    /**
     * Renders profitability as color-coded percentage
     */
    const renderProfitabilityCell = (params: ICellRendererParams<BulkAnalysisResult>): React.ReactNode => {
        if (!params.data?.scores) return null;
        
        const value = params.data.scores.totalPositiveScore;
        const color = value >= 70 ? '#4caf50' : value >= 50 ? '#ff9800' : '#f44336';
        return (
            <Box sx={{
                color: color,
                display: 'flex',
                alignItems: 'center',   // vertical center
                justifyContent: 'center', // horizontal center
                height: '100%',
                textAlign: 'center',
            }}
            >
                {value.toFixed(1)}
            </Box>
        );
    };

    /**
     * Renders risk level as color-coded percentage
     */
    const renderRiskCell = (params: ICellRendererParams<BulkAnalysisResult>): React.ReactNode => {
        if (!params.data?.scores) return null;

        const value = params.data.scores.totalRiskScore;
        const color = value <= 30 ? '#4caf50' : value <= 50 ? '#ff9800' : '#f44336';
        return (
            <Box
                sx={{
                    color: color,
                    display: 'flex',
                    alignItems: 'center',   // vertical center
                    justifyContent: 'center', // horizontal center
                    height: '100%',
                    textAlign: 'center',
                }}
            >
                {value.toFixed(1)}
            </Box>
        );
    };

    /**
     * Column definitions for the AgGrid table
     */
    const colDefs: (ColDef<BulkAnalysisResult> | ColGroupDef<BulkAnalysisResult>)[] = [
        {
            headerName: 'Event',
            minWidth: 300,
            valueGetter: (params) => params.data?.eventName || '',
            cellRenderer: renderEventCell,
        },
        {
            headerName: 'Date',
            field: 'eventDate',
            minWidth: 120,
            maxWidth: 160,
            cellRenderer: renderDateCell,
            sortable: true
        },
        {
            headerName: 'Venue',
            field: 'venue',
            minWidth: 290,
            cellRenderer: renderVenueCell,
        },
        {
            headerName: 'Overall Score',
            minWidth: 110,
            maxWidth: 140,
            valueGetter: (params) => params.data?.scores?.overallScore ?? null,
            cellRenderer: renderScoreChip,
            sortable: true
        },
        {
            headerName: 'Recommendation',
            minWidth: 120,
            maxWidth: 140,
            valueGetter: (params) => params.data?.recommendation?.decision || '',
            cellRenderer: renderRecommendationCell,
        },
        {
            headerName: 'Profitability (%)',
            headerClass: 'myag-header-cell-wrap-text',
            minWidth: 80,
            maxWidth: 110,
            valueGetter: (params) => params.data?.scores?.totalPositiveScore ?? null,
            cellRenderer: renderProfitabilityCell,
        },
        {
            headerName: 'Risk Level (%)',
            headerClass: 'myag-header-cell-wrap-text',
            minWidth: 80,
            maxWidth: 110,
            valueGetter: (params) => params.data?.scores?.totalRiskScore ?? null,
            cellRenderer: renderRiskCell,
        },
        {
            headerName: 'Primary Days to Sellout',
            headerClass: 'myag-header-cell-wrap-text',
            cellClass: 'myag-align-right',
            minWidth: 80,
            maxWidth: 150,
            valueGetter: (params) => params.data?.velocitySnapshot?.primary_days_to_sellout ?? null,
            valueFormatter: (params) => params.value !== null && params.value !== undefined && typeof params.value === 'number' ? params.value.toFixed(2) : '',
            tooltipValueGetter: (params) => (params.data?.velocitySnapshot?.primary_days_to_sellout ?? '').toString(),
            sortable: true,
        },
        {
            headerName: 'Days before Event Sells Out',
            headerClass: 'myag-header-cell-wrap-text',
            cellClass: 'myag-align-right',
            minWidth: 70,
            maxWidth: 150,
            valueGetter: (params) => params.data?.velocitySnapshot?.days_before_event_sells_out ?? null,
            valueFormatter: (params) => params.value !== null && params.value !== undefined && typeof params.value === 'number' ? params.value.toFixed(2) : '',
            tooltipValueGetter: (params) => (params.data?.velocitySnapshot?.days_before_event_sells_out ?? '').toString(),
            sortable: true,
        },
        {
            headerName: 'TN sales velocity all time',
            headerClass: 'myag-header-cell-wrap-text',
            cellClass: 'myag-align-right',
            minWidth: 70,
            maxWidth: 130,
            valueGetter: (params) => params.data?.velocitySnapshot?.tn_all_time_number_of_sales ?? null,
            sortable: true,
        },
        {
            headerName: 'Venue Total Sales (Spec + TN total)',
            headerClass: 'myag-header-cell-wrap-text',
            cellClass: 'myag-align-right',
            minWidth: 70,
            maxWidth: 140,
            valueGetter: (params) => params.data?.velocitySnapshot?.venue_total_sales ?? null,
            sortable: true,
        }
    ];

    /**
     * Runs bulk analysis on provided event IDs.
     * Calls the real-ticket API to perform analysis.
     * Extracts city and state information from SpiderEvents data and enriches EventItem objects.
     *
     * @param events - Array of events to analyze (up to 10)
     * @returns Promise resolving to bulk analysis results
     */
    const runBulkAnalysis = useCallback(async (events: RawEventData[]): Promise<BulkAnalysisResult[]> => {
        if (!events || events.length === 0) {
            notify('No events to analyze. Please provide events.', { type: 'warning' });
            return [];
        }
        try {
            // PrepareScoreEvents now returns RTP_EventInputDto[] directly from the backend
            // No need for frontend mapping - the backend handles this via BulkAnalysisService
            const prepareResp = await dataProvider.postData<PrepareData[]>('RTP_Event', 'PrepareScoreEvents', events);
            const preData = prepareResp.data;

            if (preData.length === 0) {
                notify('No valid events to analyze after preparation. Please check event data.', { type: 'warning' });
                return [];
            }

            const user = await dataProvider.currentUser();
            const userName = user.email || 'unknown';
            // Use the selected events for bulk analysis
            const response = await fetch(`${REAL_TICKET_API_URL}/bulk-score`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ events: preData, userName })
            });
            
            const results = await response.json() as BulkAnalysisResult[];
            
            // Enrich results with city and state from the original events
            results.forEach((result) => {
                const originalEvent = preData.find((pre) => pre.event?.id === result.eventId);
                if (originalEvent) {
                    result.url = originalEvent.event?.url || '';
                    result.eventDate = originalEvent.event?.dateTime || '';
                    result.venue = originalEvent.event?.venue?.trim() || '';
                    result.city = originalEvent.event?.city?.trim();
                    result.state = originalEvent.event?.state?.trim();
                    result.country = originalEvent.event?.country?.trim();
                }
            });

            // Update recommendation counts based on new results
            setRecommendationCount(
                recommendationFilterData.map(x => x.name)
                    .map(name => ({
                        name,
                        count: results.filter(r => r.recommendation.decision === name).length
                    }))
            );
            
            return results;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            notify('Failed to run bulk analysis: ' + errorMessage, { type: 'error' });
            return [];
        }
    }, [dataProvider, notify, REAL_TICKET_API_URL, selectedEvents]);

    /**
     * Exposes methods to parent component via ref
     */
    useImperativeHandle(ref, () => ({
        runBulkAnalysis,
    }), [runBulkAnalysis]);

    /**
     * Renders the empty state when no bulk analysis data is available
     * @returns Empty state component
     */
    const renderEmptyState = (): React.ReactElement => (
        <Box sx={{ textAlign: 'center', py: 4 }}>
            <TrendingUpIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
                No Bulk Analysis Available
            </Typography>
            <Typography variant="body2" color="textSecondary">
                Run bulk analysis from the Event Search tab
            </Typography>
        </Box>
    );

    /**
     * Renders bulk analysis results using AgGrid following SearchEventTable pattern
     * @returns AgGrid component with bulk analysis data
     */
    const renderResultsTable = (): React.ReactElement => (
        <Box sx={{ width: '100%', mb: 2, mt: 1 }}>
            <AgGrid
                list={'BulkAnalysis'}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                rowHeight={60}
                rowSelection='single'
                rowData={filteredBulkAnalysis}
                isRaw={true}
                hidePaging={true}
            />
        </Box>
    );

    /**
     * Renders loading indicator during bulk analysis operations
     * @returns Loading component
     */
    const renderLoadingState = (): React.ReactElement => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ mr: 2 }} />
            <Typography>Running bulk analysis...</Typography>
        </Box>
    );

    /**
     * Renders the color-coded recommendation filter with instant search.
     * Uses custom checkbox group implementation that:
     * - Applies color coding to each label based on score color mapping
     * - Triggers filtering instantly on checkbox change (no search button required)
     * - Aligns horizontally with proper spacing
     * @returns Color-coded filter component with instant search
     */
    const renderRecommendationFilter = (): React.ReactElement => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                Filter by Recommendation:
            </Typography>
            <FormGroup row>
                {recommendationFilterData.map((item) => (
                    <FormControlLabel
                        key={item.name}
                        control={
                            <Checkbox
                                checked={recommendationFilter.includes(item.name)}
                                onChange={(e) => handleCheckboxChange(item.name, e.target.checked)}
                                size="small"
                                sx={{
                                    color: item.color,
                                    '&.Mui-checked': {
                                        color: item.color
                                    }
                                }}
                            />
                        }
                        label={
                            <Badge badgeContent={recommendationCount.find(r => r.name === item.name)?.count || 0} color={item.color} max={Infinity}>
                                <Chip
                                    label={item.label}
                                    size="small"
                                    color={item.color}
                                    variant={recommendationFilter.includes(item.name) ? 'filled' : 'outlined'}
                                    sx={{ cursor: 'pointer' }}
                                />
                            </Badge>
                        }
                        sx={{ mr: 3 }}
                    />
                ))}
            </FormGroup>
        </Box>
    );

    return (
        <Card>
            <CardContent>
                {/*
                    Layout structure using flexbox to align title and filter on the same row.
                    - Title is positioned on the left
                    - Filter control is positioned on the right
                    - Both elements are vertically centered with alignItems: 'center'
                    - Space between elements is automatically distributed with justifyContent: 'space-between'
                */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        Bulk Analysis Results
                    </Typography>
                    {/* Recommendation Filter - only shown when results are available */}
                    {bulkAnalysis.length > 0 && renderRecommendationFilter()}
                </Box>

                {isLoading ? (
                    renderLoadingState()
                ) : bulkAnalysis.length === 0 ? (
                    renderEmptyState()
                ) : (
                    renderResultsTable()
                )}
            </CardContent>
        </Card>
    );
});

BulkAnalysisTab.displayName = 'BulkAnalysisTab';

export default BulkAnalysisTab;
